import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, FolderPlus, Sparkles, Search, Star, Play, MoreHorizontal,
  Edit, Copy, Trash2, Clock, FileText, Users, Lock, Share2, CalendarClock,
  Folder, Wand2, BarChart3, LayoutDashboard, TrendingUp,
  ChevronLeft, ChevronRight, ArrowUpRight,
  Grid3X3, List, Inbox, Globe, Tag, Download, X, Home,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { getObject } from '../../analytics-shared/fixtures/reportObjects';
import { useReportsList, useReportMutations } from '../hooks/useReports';
import { useReportFolders } from '../../analytics-shared/hooks/useFolders';
import { generateReportFromPrompt, AI_PROMPT_SUGGESTIONS } from '../ai/aiAssistant';
// exportsApi import removed — ReportExportModal owns the download
// dispatch now (PDF / CSV / Excel) and we open it instead of forcing
// CSV from the dropdown.
import { createReportConfig, reportsApi } from '../api/reportsApi';
import { invalidateQueries } from '../../analytics-shared/hooks/queryShim';
import { useDashboardsList } from '../../dashboards/hooks/useDashboards';
import { VISIBILITY_META as VIS_META, CHART_TYPE_LABEL, CHART_PALETTE as CHART_COLORS } from '../../analytics-shared/constants';
import InsightCard from '../../analytics-shared/components/InsightCard';
import CreateFolderModal from '../../analytics-shared/components/CreateFolderModal';
import CreateReportModal from '../components/modals/CreateReportModal';
import ShareModal from '../../analytics-shared/components/ShareModal';
import ReportExportModal from '../../analytics-shared/components/ReportExportModal';
import MoveFolderModal from '../../analytics-shared/components/MoveFolderModal';
import { ASSET_REPORT } from '../../analytics-shared/api/sharesApi';

const fmtDate = (iso) => {
  if (!iso) return '—';
  const h = Math.floor((Date.now() - new Date(iso)) / 3600000);
  if (h < 1) return 'Just now';
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
};

const MiniSparkline = ({ reportId, chartType }) => {
  const data = useMemo(() => {
    let seed = reportId.split('').reduce((acc, c) => (acc * 31 + c.charCodeAt(0)) & 0xffff, 0);
    return Array.from({ length: 8 }, () => {
      seed = (seed * 1664525 + 1013904223) & 0x7fffffff;
      return 10 + (seed % 80);
    });
  }, [reportId]);
  const max = Math.max(...data, 1);

  if (chartType === 'line' || chartType === 'area') {
    const h = 44;
    const pts = data.map((v, i) => `${(i / 7) * 100},${h - (v / max) * (h - 4)}`).join(' ');
    const areaPath = `M 0,${h - (data[0] / max) * (h - 4)} ${data.map((v, i) => `L ${(i / 7) * 100},${h - (v / max) * (h - 4)}`).join(' ')} L 100,${h} L 0,${h} Z`;
    return (
      <svg viewBox={`0 0 100 ${h}`} className="w-full" style={{ height: 44 }} preserveAspectRatio="none">
        {chartType === 'area' && <path d={areaPath} fill="rgb(99,102,241)" opacity="0.15" />}
        <polyline points={pts} fill="none" stroke="rgb(99,102,241)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (chartType === 'pie' || chartType === 'donut') {
    return (
      <div className="flex items-end gap-0.5 h-11">
        {data.slice(0, 5).map((v, i) => (
          <div key={i} className="flex-1 rounded-sm opacity-80" style={{ height: `${(v / max) * 100}%`, backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
        ))}
      </div>
    );
  }
  if (chartType === 'funnel') {
    return (
      <div className="flex flex-col gap-1 h-11 justify-center py-0.5">
        {[100, 78, 54, 32].map((w, i) => (
          <div key={i} className="rounded-sm mx-auto" style={{ width: `${w}%`, height: 7, backgroundColor: CHART_COLORS[i] }} />
        ))}
      </div>
    );
  }
  if (chartType === 'heatmap') {
    return (
      <div className="grid grid-cols-4 gap-0.5 h-11">
        {data.slice(0, 8).map((v, i) => (
          <div key={i} className="rounded-sm" style={{ background: `rgba(99,102,241,${0.1 + (v / max) * 0.7})` }} />
        ))}
      </div>
    );
  }
  if (chartType === 'none') {
    // No charts configured — show a subtle table placeholder instead of
    // pretending there's a chart.
    return (
      <div className="flex flex-col gap-1 h-11 justify-center">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex gap-1">
            <div className="h-1.5 rounded-full bg-slate-200" style={{ width: `${28 + ((i * 17) % 20)}%` }} />
            <div className="h-1.5 rounded-full bg-slate-100 flex-1" />
          </div>
        ))}
      </div>
    );
  }
  if (chartType === 'kpi') {
    return (
      <div className="flex items-center justify-center h-11">
        <span className="text-2xl font-bold text-indigo-600">{(data[0] / 10).toFixed(1)}k</span>
      </div>
    );
  }
  return (
    <div className="flex items-end gap-0.5 h-11 w-full">
      {data.map((v, i) => (
        <div key={i} className="flex-1 rounded-t-sm" style={{ height: `${(v / max) * 100}%`, backgroundColor: `rgba(99,102,241,${0.25 + (v / max) * 0.55})` }} />
      ))}
    </div>
  );
};

const ReportCard = ({ report: r, selected, onSelect, onRun, onFavorite, onEdit, onClone, onDelete, onExport, onShare, onMove }) => {
  const obj = getObject(r.object);
  const vis = VIS_META[r.visibility] || VIS_META.Private;
  const VisIcon = vis.icon;
  const chartType = r.charts?.length ? r.charts[0].type : 'none';
  return (
    <div
      onClick={() => onSelect(r)}
      className={`relative flex flex-col bg-white rounded-2xl border transition-all duration-200 cursor-pointer group overflow-hidden ${
        selected ? 'border-indigo-400 shadow-lg ring-1 ring-indigo-300/50' : 'border-slate-100 shadow-sm hover:shadow-lg hover:border-indigo-200/70'
      }`}
    >
      <div className="px-4 pt-4 pb-2">
        <div className="bg-slate-50/70 rounded-xl p-2.5 border border-slate-100/80">
          <MiniSparkline reportId={r.id} chartType={chartType} />
        </div>
      </div>

      <div className="px-4 pb-4 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-1 mb-1">
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-slate-800 group-hover:text-indigo-700 truncate leading-snug transition-colors">{r.name}</h3>
          </div>
          <button onClick={(e) => { e.stopPropagation(); onFavorite(r); }} className="flex-shrink-0 p-0.5 -mt-0.5">
            <Star className={`w-4 h-4 transition-colors ${r.favorite ? 'fill-amber-400 text-amber-400' : 'text-slate-300 hover:text-amber-400'}`} />
          </button>
        </div>

        {r.description && <p className="text-[11px] text-slate-500 leading-relaxed line-clamp-2 mb-2">{r.description}</p>}

        <div className="flex items-center gap-1.5 flex-wrap mb-3">
          {obj && <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full">{obj.label}</span>}
          <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full">
            {CHART_TYPE_LABEL[chartType] || chartType}
          </span>
          {r.schedule && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full">
              <CalendarClock className="w-2.5 h-2.5" />{r.schedule.frequency}
            </span>
          )}
          <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${vis.cls}`}>
            <VisIcon className="w-2.5 h-2.5" />{vis.label}
          </span>
        </div>

        <div className="mt-auto flex items-center justify-between">
          <div className="text-[10px] text-slate-400 flex items-center gap-2">
            <span>{r.createdBy}</span><span>·</span><span>{fmtDate(r.updatedAt)}</span>
          </div>
        </div>

        {/* Always-visible card actions (Salesforce-style). Previously
            these only appeared on hover, which was a discoverability
            problem on touch devices and slowed quick scanning. */}
        <div className="mt-3 flex items-center gap-2">
          <button onClick={(e) => { e.stopPropagation(); onRun(r); }}
            className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors">
            <Play className="w-3 h-3" /> Run
          </button>
          <button onClick={(e) => { e.stopPropagation(); onEdit(r); }}
            className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 transition-colors">
            <Edit className="w-3 h-3" /> Edit
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button onClick={(e) => e.stopPropagation()} className="p-1.5 border border-slate-200 rounded-xl text-slate-500 hover:bg-slate-50 transition-colors">
                <MoreHorizontal className="w-3.5 h-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onShare?.(r); }}>
                <Share2 className="w-3.5 h-3.5 mr-2" /> Share…
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onMove?.(r); }}>
                <Folder className="w-3.5 h-3.5 mr-2" /> Move…
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onClone(r); }}>
                <Copy className="w-3.5 h-3.5 mr-2" /> Clone
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onExport(r); }}>
                <Download className="w-3.5 h-3.5 mr-2" /> Export…
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDelete(r); }} className="text-red-600 focus:text-red-600">
                <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};

const ReportListRow = ({ report: r, selected, onSelect, onRun, onFavorite, onEdit, onClone, onDelete, onExport, onShare, onMove }) => {
  const obj = getObject(r.object);
  const vis = VIS_META[r.visibility] || VIS_META.Private;
  const VisIcon = vis.icon;
  const chartType = r.charts?.length ? r.charts[0].type : 'none';
  return (
    <div onClick={() => onSelect(r)}
      className={`flex items-center gap-4 px-4 py-3 border-b border-slate-100 last:border-0 cursor-pointer group transition-colors ${
        selected ? 'bg-indigo-50/60' : 'hover:bg-slate-50/80'
      }`}>
      <button onClick={(e) => { e.stopPropagation(); onFavorite(r); }} className="flex-shrink-0">
        <Star className={`w-4 h-4 ${r.favorite ? 'fill-amber-400 text-amber-400' : 'text-slate-300 hover:text-amber-400'}`} />
      </button>
      <div className="w-14 flex-shrink-0 bg-slate-50 rounded-lg p-1 border border-slate-100">
        <MiniSparkline reportId={r.id} chartType={chartType} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-800 group-hover:text-indigo-700 truncate transition-colors">{r.name}</p>
        {r.description && <p className="text-xs text-slate-400 truncate">{r.description}</p>}
      </div>
      <div className="w-28 flex-shrink-0 hidden md:block">
        <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">{obj?.label || r.object}</span>
      </div>
      <div className="w-28 flex-shrink-0 text-xs text-slate-500 hidden lg:block truncate">{r.createdBy}</div>
      <div className="w-24 flex-shrink-0 text-xs text-slate-500 hidden xl:block">{fmtDate(r.updatedAt)}</div>
      <div className="w-24 flex-shrink-0 hidden lg:flex">
        {r.schedule ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full">
            <CalendarClock className="w-2.5 h-2.5" />{r.schedule.frequency}
          </span>
        ) : <span className="text-xs text-slate-300">—</span>}
      </div>
      <div className="w-20 flex-shrink-0 hidden md:flex">
        <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${vis.cls}`}>
          <VisIcon className="w-2.5 h-2.5" />{r.visibility}
        </span>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button onClick={(e) => { e.stopPropagation(); onRun(r); }}
          className="flex items-center gap-1 px-2.5 py-1 text-xs font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
          <Play className="w-3 h-3" /> Run
        </button>
        <button onClick={(e) => { e.stopPropagation(); onEdit(r); }}
          className="px-2.5 py-1 text-xs font-medium border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50">
          Edit
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button onClick={(e) => e.stopPropagation()} className="p-1 hover:bg-slate-100 rounded-lg text-slate-400">
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onClick={() => onShare?.(r)}><Share2 className="w-3.5 h-3.5 mr-2" />Share…</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onMove?.(r)}><Folder className="w-3.5 h-3.5 mr-2" />Move…</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onClone(r)}><Copy className="w-3.5 h-3.5 mr-2" />Clone</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onExport(r)}><Download className="w-3.5 h-3.5 mr-2" />Export…</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onDelete(r)} className="text-red-600 focus:text-red-600"><Trash2 className="w-3.5 h-3.5 mr-2" />Delete</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

const PreviewPanel = ({ report: r, onClose, onRun, onView, onEdit, onClone, onDelete }) => {
  if (!r) return null;
  const obj = getObject(r.object);
  const vis = VIS_META[r.visibility] || VIS_META.Private;
  const VisIcon = vis.icon;
  const chartType = r.charts?.length ? r.charts[0].type : 'none';
  return (
    <div className="flex flex-col h-full bg-white border-l border-slate-200 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3.5 border-b border-slate-100 flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <div className="p-1.5 bg-indigo-100 rounded-lg flex-shrink-0">
            <BarChart3 className="w-3.5 h-3.5 text-indigo-600" />
          </div>
          <span className="text-sm font-bold text-slate-800 truncate">Report Details</span>
        </div>
        <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0">
          <X className="w-4 h-4 text-slate-500" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="px-4 pt-4 pb-2">
          <div className="bg-gradient-to-br from-slate-50 to-indigo-50/30 rounded-2xl p-4 border border-slate-100">
            <MiniSparkline reportId={r.id} chartType={chartType} />
          </div>
        </div>

        <div className="px-4 py-3">
          <div className="flex items-start gap-2 mb-1">
            <button onClick={() => onView(r)} className="flex items-center gap-1 text-[10px] text-indigo-600 hover:underline ml-auto">
              <ArrowUpRight className="w-3 h-3" /> Open
            </button>
          </div>
          <h2 className="text-base font-bold text-slate-800 leading-snug">{r.name}</h2>
          {r.description && <p className="text-xs text-slate-500 mt-1 leading-relaxed">{r.description}</p>}
        </div>

        <div className="px-4 pb-3">
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: 'Object', value: obj?.label || r.object },
              { label: 'Chart Type', value: CHART_TYPE_LABEL[chartType] || chartType },
              { label: 'Folder', value: r.folder || '—' },
              { label: 'Created By', value: r.createdBy },
              { label: 'Last Modified', value: fmtDate(r.updatedAt) },
              { label: 'Last Run', value: fmtDate(r.lastRun) },
            ].map(({ label, value }) => (
              <div key={label} className="bg-slate-50 rounded-xl p-2.5">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">{label}</p>
                <p className="text-xs font-semibold text-slate-700 mt-0.5 truncate">{value}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="px-4 pb-3 flex flex-wrap gap-1.5">
          <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2.5 py-1 rounded-full ${vis.cls}`}>
            <VisIcon className="w-3 h-3" />{r.visibility}
          </span>
          {r.schedule && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-full">
              <CalendarClock className="w-3 h-3" />{r.schedule.frequency} · {r.schedule.format}
            </span>
          )}
          {r.tags?.map((t) => (
            <span key={t} className="inline-flex items-center gap-1 text-[10px] font-medium px-2.5 py-1 bg-indigo-50 text-indigo-600 rounded-full">
              <Tag className="w-2.5 h-2.5" />{t}
            </span>
          ))}
        </div>

        {r.columns?.length > 0 && (
          <div className="px-4 pb-4">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-2">Fields ({r.columns.length})</p>
            <div className="flex flex-wrap gap-1">
              {r.columns.map((col) => (
                <span key={col} className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-md font-mono">{col}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="px-4 pb-4 pt-2 flex flex-col gap-2 flex-shrink-0 border-t border-slate-100">
        <button onClick={() => onRun(r)}
          className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-bold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors shadow-sm">
          <Play className="w-4 h-4" /> Run Report
        </button>
        <div className="flex gap-2">
          <button onClick={() => onEdit(r)}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50">
            <Edit className="w-3.5 h-3.5" /> Edit
          </button>
          <button onClick={() => onClone(r)}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50">
            <Copy className="w-3.5 h-3.5" /> Clone
          </button>
          <button onClick={() => onDelete(r)}
            className="py-2 px-3 text-xs font-semibold border border-red-200 text-red-600 rounded-xl hover:bg-red-50 transition-colors">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

const AiModal = ({ open, onClose, onGenerate }) => {
  const [prompt, setPrompt] = useState('');
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="px-6 py-5 border-b border-slate-100">
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl shadow-sm">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-base font-bold text-slate-800">AI Report Generator</h2>
          </div>
          <p className="text-sm text-slate-500 ml-11">Describe what you want — AI builds the full report config</p>
        </div>
        <div className="p-6">
          <textarea
            autoFocus
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && e.metaKey) onGenerate(prompt); }}
            placeholder="e.g. Show monthly closed deals grouped by sales rep with revenue trend…"
            className="w-full h-24 border border-slate-200 rounded-2xl p-4 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-slate-50 leading-relaxed"
          />
          <p className="text-xs text-slate-400 mt-2 mb-3">Quick prompts:</p>
          <div className="flex flex-wrap gap-1.5 mb-4">
            {AI_PROMPT_SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => setPrompt(s)}
                className="text-xs bg-slate-100 hover:bg-indigo-50 hover:text-indigo-700 text-slate-600 px-2.5 py-1 rounded-full transition-colors border border-transparent hover:border-indigo-200">
                {s}
              </button>
            ))}
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 py-2.5 text-sm font-medium text-slate-600 border border-slate-200 rounded-2xl hover:bg-slate-50">Cancel</button>
            <button
              onClick={() => { if (prompt.trim()) { onGenerate(prompt); setPrompt(''); } }}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-bold bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl hover:from-indigo-700 hover:to-purple-700 shadow-sm">
              <Wand2 className="w-4 h-4" /> Generate Report
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const NavItem = ({ icon: Icon, label, count, active, onClick, collapsed, indent = false }) => (
  <button
    onClick={onClick}
    title={collapsed ? label : undefined}
    className={`w-full flex items-center gap-2.5 rounded-xl text-sm transition-all duration-150 mb-0.5 ${
      collapsed ? 'px-2 py-2.5 justify-center' : `px-3 py-2 ${indent ? 'pl-8' : ''}`
    } ${active ? 'bg-indigo-600 text-white shadow-sm shadow-indigo-200' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}>
    <Icon className={`flex-shrink-0 ${collapsed ? 'w-5 h-5' : 'w-4 h-4'}`} />
    {!collapsed && (
      <>
        <span className="flex-1 text-left font-medium truncate">{label}</span>
        {count !== undefined && (
          <span className={`text-xs rounded-full px-1.5 min-w-[20px] text-center ${active ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'}`}>{count}</span>
        )}
      </>
    )}
  </button>
);

const OBJECTS_LIST = ['deals', 'leads', 'contacts', 'companies', 'tickets', 'campaigns', 'invoices', 'tasks', 'calls', 'meetings'];

const ReportsListPage = () => {
  const navigate = useNavigate();
  const { reports, refetch: refetchReports } = useReportsList();
  const { folders, create: createFolder } = useReportFolders();
  const { data: dashboards = [] } = useDashboardsList();
  const { remove, duplicate, toggleFavorite, save } = useReportMutations();

  const [view, setView] = useState('all');
  const [search, setSearch] = useState('');
  const [objectFilter, setObjectFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  // Folder modal state. `null` = closed. `{ mode: 'create' }` opens for
  // create. We deliberately drive open/close through this state rather
  // than a boolean so future rename/delete flows can reuse the same
  // dialog component with `{ mode: 'rename', id, initialName }`.
  const [folderModal, setFolderModal] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  // shareTarget: the report row whose Share modal is currently open. We
  // keep this as the full report rather than just an id so the modal can
  // render the asset name and owner row without a second fetch.
  const [shareTarget, setShareTarget] = useState(null);
  // exportTarget: the report whose ReportExportModal is currently open.
  // Stored as the full doc rather than just the id so the modal can
  // render the asset name without a refetch.
  const [exportTarget, setExportTarget] = useState(null);
  // moveTarget: the report whose MoveFolderModal is currently open.
  // Holds the full doc so the modal can show the asset name + current
  // folder pre-selected. Reports don't yet have a dedicated
  // moveToFolder mutation hook, so the handler uses reportsApi.save +
  // an invalidateQueries(['reports']) to refresh the list cache.
  const [moveTarget, setMoveTarget] = useState(null);

  const counts = useMemo(() => ({
    all: reports.length,
    recent: reports.filter((r) => r.lastRun).length,
    favorites: reports.filter((r) => r.favorite).length,
    scheduled: reports.filter((r) => r.schedule).length,
    team: reports.filter((r) => r.visibility === 'Team').length,
    // ACL-driven: matches actual share records (shared-with-me OR
    // shared-by-me) rather than the visibility string.
    shared: reports.filter((r) => r.shared).length,
    private: reports.filter((r) => r.visibility === 'Private').length,
  }), [reports]);

  const filtered = useMemo(() => {
    let list = [...reports];
    if (view === 'recent') list = list.filter((r) => r.lastRun).sort((a, b) => new Date(b.lastRun) - new Date(a.lastRun));
    else if (view === 'favorites') list = list.filter((r) => r.favorite);
    else if (view === 'scheduled') list = list.filter((r) => r.schedule);
    else if (view === 'team') list = list.filter((r) => r.visibility === 'Team');
    else if (view === 'shared') list = list.filter((r) => r.shared);
    else if (view === 'private') list = list.filter((r) => r.visibility === 'Private');
    else if (view.startsWith('folder:')) list = list.filter((r) => r.folder === view.slice(7));

    if (search) list = list.filter((r) => r.name.toLowerCase().includes(search.toLowerCase()) || r.description?.toLowerCase().includes(search.toLowerCase()));
    if (objectFilter !== 'all') list = list.filter((r) => r.object === objectFilter);
    if (tagFilter !== 'all') list = list.filter((r) => (r.tags || []).includes(tagFilter));
    if (dateFilter !== 'all') {
      const days = Number(dateFilter);
      const cutoff = Date.now() - days * 86400000;
      list = list.filter((r) => {
        const t = new Date(r.updatedAt || r.createdAt || 0).getTime();
        return t >= cutoff;
      });
    }
    return list;
  }, [reports, view, search, objectFilter, tagFilter, dateFilter]);

  // Union of tags across all reports — drives the tag filter dropdown.
  const allTags = useMemo(() => {
    const set = new Set();
    reports.forEach((r) => (r.tags || []).forEach((t) => set.add(t)));
    return [...set].sort();
  }, [reports]);

  const handleStart = async (objectKey, reportType = null) => {
    // Draft only — nothing persists until the user explicitly saves in
    // the builder. The builder picks the draft up from sessionStorage.
    const report = createReportConfig(objectKey, reportType);
    try { sessionStorage.setItem('cluvik.pendingDraft', JSON.stringify(report)); } catch { /* ignore */ }
    navigate('/reports/builder');
  };
  const handleNewFolder = () => setFolderModal({ mode: 'create' });
  // Modal-side handler — awaited by CreateFolderModal so it can show a
  // spinner and surface any thrown error inline. We do NOT close the
  // modal in the catch path; CreateFolderModal handles error display.
  const handleFolderCreate = async (name) => {
    await createFolder.mutate(name);
    setFolderModal(null);
    toast.success(`Folder "${name}" created`);
  };
  const handleAiGenerate = async (prompt) => {
    if (!prompt.trim()) { toast.error('Describe the report you want'); return; }
    const partial = generateReportFromPrompt(prompt);
    const report = { ...createReportConfig(partial.object), ...partial };
    const saved = await save.mutate(report);
    toast.success('AI report generated');
    setAiOpen(false);
    navigate(`/reports/builder/${saved.id}`);
  };
  const handleRun = useCallback(async (r) => {
    await reportsApi.markRun(r.id);
    refetchReports();
    navigate(`/reports/view/${r.id}?run=1`);
  }, [navigate, refetchReports]);
  const handleEdit = useCallback((r) => navigate(`/reports/builder/${r.id}`), [navigate]);
  const handleClone = useCallback((r) => duplicate.mutate(r.id).then(() => toast.success('Report cloned')), [duplicate]);
  const handleDelete = useCallback((r) => {
    if (!window.confirm(`Delete "${r.name}"? This cannot be undone.`)) return;
    remove.mutate(r.id).then(() => {
      if (selectedReport?.id === r.id) setSelectedReport(null);
      toast.success('Report deleted');
    });
  }, [remove, selectedReport]);
  const handleFavorite = useCallback((r) => toggleFavorite.mutate(r.id), [toggleFavorite]);
  const handleShare = useCallback((r) => setShareTarget(r), []);
  // Opens the format-picker modal instead of force-CSV. The modal owns
  // the actual download dispatch so success/error toasts + duplicate-
  // click protection live in one place.
  const handleExport = useCallback((r) => setExportTarget(r), []);
  const handleMove = useCallback((r) => setMoveTarget(r), []);
  const handleSelect = useCallback((r) => setSelectedReport((prev) => prev?.id === r.id ? null : r), []);

  return (
    <div className="h-full flex flex-col bg-[#f4f5f8] overflow-hidden">
      <div className="flex-shrink-0 bg-white/95 backdrop-blur-sm border-b border-slate-200 px-4 py-2.5 flex items-center gap-3 z-30 shadow-sm">
        <button onClick={() => setSidebarOpen((v) => !v)} className="p-2 hover:bg-slate-100 rounded-xl text-slate-500 transition-colors flex-shrink-0">
          {sidebarOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="p-1.5 bg-indigo-100 rounded-lg"><BarChart3 className="w-4 h-4 text-indigo-600" /></div>
          <span className="text-sm font-bold text-slate-800">Reports</span>
          <span className="text-slate-300">·</span>
          <span className="text-xs text-slate-400">Amroar CRM</span>
        </div>

        <div className="flex-1 max-w-md relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search reports, folders, charts…"
            className="w-full pl-10 pr-4 py-2 text-sm border border-slate-200 rounded-xl bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-colors" />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 ml-auto flex-shrink-0">
          <button onClick={() => navigate('/crm-platform')}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 hover:text-indigo-600 transition-colors"
            title="Back to CRM">
            <Home className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Back to CRM</span>
          </button>
          <button onClick={() => setAiOpen(true)}
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-indigo-700 border border-indigo-200 bg-indigo-50 rounded-xl hover:bg-indigo-100 transition-colors">
            <Sparkles className="w-3.5 h-3.5" /> AI Generate
          </button>
          <button onClick={() => navigate('/reports/dashboards')}
            className="hidden md:flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors">
            <LayoutDashboard className="w-3.5 h-3.5" /> Dashboards
          </button>
          <button onClick={() => setCreateOpen(true)}
            className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-bold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors shadow-sm">
            <Plus className="w-3.5 h-3.5" /> New Report
          </button>
        </div>
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        <div className={`flex-shrink-0 bg-white border-r border-slate-200 flex flex-col transition-all duration-200 overflow-hidden ${sidebarOpen ? 'w-56' : 'w-14'}`}>
          <div className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
            {sidebarOpen && <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-3 pt-1 pb-1.5">Overview</p>}
            <NavItem icon={FileText} label="All Reports" count={counts.all} active={view === 'all'} onClick={() => setView('all')} collapsed={!sidebarOpen} />
            <NavItem icon={BarChart3} label="Analytics" active={false} onClick={() => navigate('/reports')} collapsed={!sidebarOpen} />
            <NavItem icon={LayoutDashboard} label="Dashboards" count={dashboards.length} active={false} onClick={() => navigate('/reports/dashboards')} collapsed={!sidebarOpen} />

            {sidebarOpen && <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-3 pt-3 pb-1.5">My Reports</p>}
            {!sidebarOpen && <div className="h-2" />}
            <NavItem icon={Clock} label="Recent" count={counts.recent} active={view === 'recent'} onClick={() => setView('recent')} collapsed={!sidebarOpen} />
            <NavItem icon={Star} label="Favorites" count={counts.favorites} active={view === 'favorites'} onClick={() => setView('favorites')} collapsed={!sidebarOpen} />
            <NavItem icon={CalendarClock} label="Scheduled" count={counts.scheduled} active={view === 'scheduled'} onClick={() => setView('scheduled')} collapsed={!sidebarOpen} />
            <NavItem icon={Users} label="Team Reports" count={counts.team} active={view === 'team'} onClick={() => setView('team')} collapsed={!sidebarOpen} />
            <NavItem icon={Share2} label="Shared" count={counts.shared} active={view === 'shared'} onClick={() => setView('shared')} collapsed={!sidebarOpen} />

            {sidebarOpen && (
              <div className="flex items-center justify-between px-3 pt-3 pb-1.5">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Folders</p>
                <button onClick={handleNewFolder} className="text-slate-400 hover:text-indigo-600 transition-colors" title="New folder">
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
            {!sidebarOpen && <div className="h-2" />}
            {folders.map((fld) => (
              <NavItem key={fld.id} icon={Folder} label={fld.name}
                count={reports.filter((r) => r.folder === fld.name).length}
                active={view === `folder:${fld.name}`} onClick={() => setView(`folder:${fld.name}`)} collapsed={!sidebarOpen} />
            ))}

            {sidebarOpen && <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-3 pt-3 pb-1.5">Intelligence</p>}
            {!sidebarOpen && <div className="h-2" />}
            <NavItem icon={Sparkles} label="AI Insights" active={false} onClick={() => setAiOpen(true)} collapsed={!sidebarOpen} />
            <NavItem icon={TrendingUp} label="Trends" active={false} onClick={() => {}} collapsed={!sidebarOpen} />
          </div>
        </div>

        <div className="flex flex-1 min-w-0 overflow-hidden">
          <div className="flex-1 overflow-y-auto flex flex-col min-w-0">
            <div className="flex-shrink-0 px-5 pt-5 pb-3">
              <div className="flex gap-3 overflow-x-auto pb-1">
                <InsightCard icon={FileText} label="Total Reports" value={reports.length} sub={`${counts.favorites} favorited`} color="indigo" onClick={() => setView('all')} />
                <InsightCard icon={CalendarClock} label="Scheduled" value={counts.scheduled} sub="auto-delivery" color="emerald" onClick={() => setView('scheduled')} />
                <InsightCard icon={Star} label="Favorites" value={counts.favorites} sub="starred by you" color="amber" onClick={() => setView('favorites')} />
                <InsightCard icon={LayoutDashboard} label="Dashboards" value={dashboards.length} sub="analytics views" color="purple" onClick={() => navigate('/reports/dashboards')} />
                <InsightCard icon={Clock} label="Recently Run" value={counts.recent} sub="have run history" color="blue" onClick={() => setView('recent')} />
              </div>
            </div>

            <div className="flex-shrink-0 px-5 pb-3 flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1.5 overflow-x-auto flex-wrap">
                <button onClick={() => setObjectFilter('all')}
                  className={`flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-xl transition-all ${
                    objectFilter === 'all' ? 'bg-slate-800 text-white shadow-sm' : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}>
                  All Objects
                </button>
                {OBJECTS_LIST.map((o) => {
                  const obj = getObject(o);
                  const cnt = reports.filter((r) => r.object === o).length;
                  if (!cnt) return null;
                  return (
                    <button key={o} onClick={() => setObjectFilter(objectFilter === o ? 'all' : o)}
                      className={`flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-xl transition-all ${
                        objectFilter === o ? 'bg-indigo-600 text-white shadow-sm' : 'bg-white border border-slate-200 text-slate-600 hover:border-indigo-300 hover:text-indigo-700'
                      }`}>
                      {obj?.label || o}
                      <span className={`ml-1 ${objectFilter === o ? 'opacity-70' : 'text-slate-400'}`}>{cnt}</span>
                    </button>
                  );
                })}
              </div>

              <div className="ml-auto flex items-center gap-2 flex-shrink-0">
                {allTags.length > 0 && (
                  <select value={tagFilter} onChange={(e) => setTagFilter(e.target.value)}
                    className={`h-8 text-xs border rounded-xl px-2 bg-white ${tagFilter !== 'all' ? 'border-indigo-400 text-indigo-700 font-semibold' : 'border-slate-200 text-slate-600'}`}>
                    <option value="all">All tags</option>
                    {allTags.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                )}
                <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}
                  className={`h-8 text-xs border rounded-xl px-2 bg-white ${dateFilter !== 'all' ? 'border-indigo-400 text-indigo-700 font-semibold' : 'border-slate-200 text-slate-600'}`}>
                  <option value="all">Any time</option>
                  <option value="7">Last 7 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="90">Last 90 days</option>
                </select>
                <span className="text-xs text-slate-400">
                  <span className="font-semibold text-slate-600">{filtered.length}</span> report{filtered.length !== 1 ? 's' : ''}
                </span>
                <div className="flex items-center bg-white border border-slate-200 rounded-xl overflow-hidden">
                  {[{ mode: 'grid', icon: Grid3X3 }, { mode: 'list', icon: List }].map(({ mode, icon: Icon }) => (
                    <button key={mode} onClick={() => setViewMode(mode)}
                      className={`p-2 transition-colors ${viewMode === mode ? 'bg-indigo-600 text-white' : 'text-slate-500 hover:bg-slate-50'}`}>
                      <Icon className="w-3.5 h-3.5" />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex-1 px-5 pb-6">
              {filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 text-center">
                  <div className="w-20 h-20 bg-indigo-50 rounded-3xl flex items-center justify-center mb-4">
                    {search ? <Search className="w-8 h-8 text-indigo-300" /> : <Inbox className="w-8 h-8 text-indigo-300" />}
                  </div>
                  <h3 className="text-base font-bold text-slate-600">
                    {search ? `No results for "${search}"` : 'No reports here yet'}
                  </h3>
                  <p className="text-sm text-slate-400 mt-1 max-w-xs">
                    {search ? 'Try a different term or clear the search' : 'Create your first report or start from a template'}
                  </p>
                  {!search && (
                    <div className="flex gap-3 mt-5">
                      <button onClick={() => setCreateOpen(true)} className="flex items-center gap-2 px-4 py-2 text-sm font-bold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700">
                        <Plus className="w-4 h-4" /> New Report
                      </button>
                    </div>
                  )}
                </div>
              ) : viewMode === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {filtered.map((r) => (
                    <ReportCard key={r.id} report={r}
                      selected={selectedReport?.id === r.id}
                      onSelect={handleSelect} onRun={handleRun} onFavorite={handleFavorite}
                      onEdit={handleEdit} onClone={handleClone} onDelete={handleDelete}
                      onExport={handleExport} onShare={handleShare} onMove={handleMove} />
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                  <div className="flex items-center gap-4 px-4 py-2.5 border-b border-slate-100 bg-slate-50/70">
                    <div className="w-4" />
                    <div className="w-14 flex-shrink-0" />
                    <div className="flex-1 text-[10px] font-bold text-slate-400 uppercase tracking-wide">Report</div>
                    <div className="w-28 flex-shrink-0 text-[10px] font-bold text-slate-400 uppercase tracking-wide hidden md:block">Object</div>
                    <div className="w-28 flex-shrink-0 text-[10px] font-bold text-slate-400 uppercase tracking-wide hidden lg:block">Author</div>
                    <div className="w-24 flex-shrink-0 text-[10px] font-bold text-slate-400 uppercase tracking-wide hidden xl:block">Modified</div>
                    <div className="w-24 flex-shrink-0 text-[10px] font-bold text-slate-400 uppercase tracking-wide hidden lg:block">Schedule</div>
                    <div className="w-20 flex-shrink-0 text-[10px] font-bold text-slate-400 uppercase tracking-wide hidden md:block">Visibility</div>
                    <div className="w-28 flex-shrink-0" />
                  </div>
                  {filtered.map((r) => (
                    <ReportListRow key={r.id} report={r}
                      selected={selectedReport?.id === r.id}
                      onSelect={handleSelect} onRun={handleRun} onFavorite={handleFavorite}
                      onEdit={handleEdit} onClone={handleClone} onDelete={handleDelete}
                      onExport={handleExport} onShare={handleShare} onMove={handleMove} />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className={`flex-shrink-0 transition-all duration-250 ease-in-out overflow-hidden ${selectedReport ? 'w-72 xl:w-80' : 'w-0'}`}>
            <PreviewPanel
              report={selectedReport}
              onClose={() => setSelectedReport(null)}
              onRun={handleRun}
              onView={(r) => navigate(`/reports/view/${r.id}`)}
              onEdit={handleEdit}
              onClone={handleClone}
              onDelete={handleDelete}
            />
          </div>
        </div>
      </div>

      <CreateReportModal open={createOpen} onClose={() => setCreateOpen(false)} onStart={handleStart} />
      <AiModal open={aiOpen} onClose={() => setAiOpen(false)} onGenerate={handleAiGenerate} />
      <CreateFolderModal
        open={folderModal !== null}
        mode={folderModal?.mode || 'create'}
        initialName={folderModal?.initialName || ''}
        existingNames={folders.map((f) => f.name)}
        subtitle="Organize and group related reports"
        onClose={() => setFolderModal(null)}
        onSave={handleFolderCreate}
      />

      <ShareModal
        open={!!shareTarget}
        onClose={() => setShareTarget(null)}
        assetType={ASSET_REPORT}
        assetId={shareTarget?.id}
        assetName={shareTarget?.name}
        ownerName={shareTarget?.createdBy}
        ownerId={shareTarget?.createdById}
      />

      <ReportExportModal
        open={!!exportTarget}
        onClose={() => setExportTarget(null)}
        report={exportTarget}
      />

      <MoveFolderModal
        open={!!moveTarget}
        onClose={() => setMoveTarget(null)}
        folders={folders}
        currentFolder={moveTarget?.folder}
        itemName={moveTarget?.name}
        kindLabel="Report"
        onMove={async (folderName) => {
          // Reports doesn't have a dedicated /move-to-folder endpoint;
          // the wedge-1 save endpoint accepts the full report doc, so
          // patch `folder` and round-trip. We're calling reportsApi.save
          // directly here (not the mutation hook), so kick the list
          // cache manually — the queryShim's invalidateQueries refetches
          // active subscribers, so the folder badge updates without
          // a reload.
          if (!moveTarget) return;
          await reportsApi.save({ ...moveTarget, folder: folderName });
          invalidateQueries(['reports']);
          toast.success(`Moved to "${folderName}"`);
        }}
      />
    </div>
  );
};

export default ReportsListPage;
